import { useState } from "react";

import { type DragEndEvent, DragOverlay, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { faArrowDown, faArrowRight, faArrowTurnDownLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetSmartCommentReplies } from "api/hooks/useGetSmartCommentReplies";
import { Roles } from "api/schemas/shared/role";
import {
  useDeletePatientTreatmentScheduleActivity,
  useGetPatientTreatmentScheduleWeek,
  usePutPatientTreatmentScheduleActivity,
} from "api/services/treatment/hooks";
import type {
  PutPatientTreatmentScheduleActivityArgs,
  UpdatePatientTreatmentScheduleActivityArgs,
} from "api/services/treatment/schemas/Treatment";
import { useProfileContext } from "contexts/ProfileContext";
import { useSnackbarContext } from "contexts/SnackbarContext";
import { sortActivities } from "routes/patients/utils/activityHelpers";
import { useGroupScheduleWeek } from "routes/patients/utils/hooks/useGroupScheduleWeek";
import {
  ActivityPreviewModal,
  type PreviewActivity,
} from "routes/patients/views/Treatment/components/TreatmentEditor/Modals";
import {
  ContentContainer,
  ContentContainerHeader,
  ContentContainerHeaderLabel,
  ContentContainerHeaderLeft,
} from "routes/patients/views/Treatment/components/TreatmentEditor/styles";
import { CurrentBadge } from "routes/patients/views/Treatment/components/TreatmentEditor/WeekTimeline/WeekRow";
import { CDndContext } from "shared/atoms/DragAndDrop";
import { Notification } from "shared/atoms/Notification";
import { Spinner } from "shared/atoms/Spinner";
import { useLocalizedDate } from "utils/date/useLocalizedDate";
import { reportError } from "utils/errorReporting";
import { userIsPartner } from "utils/profile/profileHelper";

import { DayColumn } from "./DayColumn";
import { ProtocolTimelineCard } from "./ProtocolTimelineCard";
import type { SortableProtocolCard } from "./types";
import { isExercise, isLesson } from "./utils";

interface ProtocolTimelineProps {
  patient: {
    id: number;
    treatmentID: number;
    week: number;
  };
  weekNumber: number;
}

export const ProtocolTimeline: React.FC<ProtocolTimelineProps> = ({ patient, weekNumber }) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { convertProtocolCardToSortable, groupData } = useGroupScheduleWeek();
  const { format, isToday, isBefore, parseISO, startOfToday } = useLocalizedDate();
  const { showSnackbar } = useSnackbarContext();
  const [activeActivity, setActiveActivity] = useState<SortableProtocolCard | null>(null);
  const [orientation, setOrientation] = useState<"auto" | "horizontal" | "vertical">("horizontal");
  const [previewActivity, setPreviewActivity] = useState<PreviewActivity | null>(null);

  invariant(profile);

  const putPatientTreatmentScheduleActivity = usePutPatientTreatmentScheduleActivity();
  const deletePatientTreatmentScheduleActivity = useDeletePatientTreatmentScheduleActivity();

  const { data, isError, isLoading } = useGetPatientTreatmentScheduleWeek({
    locale: profile.locale as string,
    patient_id: patient.id,
    week: weekNumber,
    treatment_id: patient.treatmentID,
    role: userIsPartner(profile) ? Roles.ClinicPartner : Roles.Therapist,
  });

  const { data: smartReplies } = useGetSmartCommentReplies({
    therapistId: profile?.id,
    patientId: patient.id,
  });

  const getSmartReply = (user_activity_id: number | null): string | null | undefined => {
    if (user_activity_id && smartReplies?.comments && smartReplies.comments.length > 0) {
      return smartReplies.comments.find(reply => reply.user_activity_id === user_activity_id)?.body;
    }
    return null;
  };

  const getSmartReplyId = (user_activity_id: number | null): number | null | undefined => {
    if (user_activity_id && smartReplies?.comments && smartReplies.comments.length > 0) {
      return smartReplies.comments.find(reply => reply.user_activity_id === user_activity_id)?.id;
    }
    return null;
  };

  const showTreatmentUpdatedMessage = () => {
    showSnackbar({
      message: t("treatment.editor.feedback.treatment_updated"),
      severity: "success",
    });
  };

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const sourceData = active.data.current;

    if (sourceData?.type === "protocol_timeline_card") {
      const activity = sourceData.activity as SortableProtocolCard;
      setActiveActivity(activity);
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveActivity(null);
      return;
    }

    const sourceData = active.data.current;
    const targetData = over.data.current;

    if (
      (sourceData?.activityType === "exercise" || sourceData?.activityType === "lesson") &&
      targetData?.droppableType === "protocol_timeline_day"
    ) {
      const activity = sourceData.activity as SortableProtocolCard;
      const targetWeek = targetData.weekNumber as number;
      const targetDay = targetData.day as number;

      if (activity.day !== targetDay && patient?.id && activity?.user_activity_schedule_id) {
        onMoveProtocolActivity({
          patientID: patient.id,
          userActivityScheduleID: activity.user_activity_schedule_id,
          payload: {
            day_number: targetDay,
            week_number: targetWeek,
          },
        });
      }
    }

    setActiveActivity(null);
  };

  const onMoveProtocolActivity = async (args: PutPatientTreatmentScheduleActivityArgs) => {
    try {
      const { patientID, userActivityScheduleID } = args;

      await putPatientTreatmentScheduleActivity.mutateAsync({
        patientID,
        userActivityScheduleID,
        payload: args.payload,
      });
      showTreatmentUpdatedMessage();
    } catch (error) {
      reportError("Error moving activity between days in protocol", new Error(JSON.stringify({ error })));
    }
  };

  const onRemoveProtocolActivity = async (args: UpdatePatientTreatmentScheduleActivityArgs) => {
    try {
      const { patientID, userActivityScheduleID } = args;

      await deletePatientTreatmentScheduleActivity.mutateAsync({
        patientID,
        userActivityScheduleID,
      });
      showTreatmentUpdatedMessage();
    } catch (error) {
      reportError("Error removing activity from protocol", new Error(JSON.stringify({ error, variables: args })));
    }
  };

  const handlePreview = (activity: SortableProtocolCard) => {
    if (isExercise(activity)) {
      setPreviewActivity({
        id: activity.activityID,
        part: activity.current_level,
        type: "exercise",
      });
    } else if (isLesson(activity)) {
      setPreviewActivity({
        id: activity.activityID,
        part: activity.part,
        type: "lesson",
      });
    }
  };

  const isCurrentWeek = weekNumber === patient.week;

  if (isLoading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
        <Spinner />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
        <Notification variant="danger">{t("errors.generic")}</Notification>
      </Box>
    );
  }

  const days = groupData(data);

  return (
    <Box sx={{ margin: "24px 30px" }}>
      <ContentContainer>
        <ContentContainerHeader className={clsx({ "is-current": isCurrentWeek })}>
          <ContentContainerHeaderLeft>
            <ContentContainerHeaderLabel>
              {t("common.week_num", { number: weekNumber })}
              {isCurrentWeek && <CurrentBadge>{t("common.current")}</CurrentBadge>}
            </ContentContainerHeaderLabel>
          </ContentContainerHeaderLeft>
          <ToggleButtonGroup
            value={orientation}
            exclusive
            onChange={(_, newOrientation) => {
              if (newOrientation !== null) {
                setOrientation(newOrientation);
              }
            }}
          >
            <ToggleButton value="horizontal">
              <FontAwesomeIcon icon={faArrowRight} />
            </ToggleButton>
            <ToggleButton value="auto">
              <FontAwesomeIcon icon={faArrowTurnDownLeft} />
            </ToggleButton>
            <ToggleButton value="vertical">
              <FontAwesomeIcon icon={faArrowDown} />
            </ToggleButton>
          </ToggleButtonGroup>
        </ContentContainerHeader>

        <CDndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <DaysScrollContainer $isVertical={orientation === "vertical"}>
            <DaysContainer className={orientation}>
              {days.map((day, index) => {
                const dayNumber = index + 1;
                const isCurrent = isToday(parseISO(day.date));
                const isBeforeCurrent = isBefore(parseISO(day.date), startOfToday());
                const sortedActivities = day.activities.sort(sortActivities);

                return (
                  <DayColumn
                    key={day.date}
                    day={dayNumber}
                    dayName={format(parseISO(day.date), "ccc")}
                    dateLabel={format(parseISO(day.date), t("common.date.noYear"))}
                    isVertical={orientation === "vertical"}
                    weekNumber={weekNumber}
                    isCurrent={isCurrent}
                    isBeforeCurrent={isBeforeCurrent}
                  >
                    <SortableContext items={sortedActivities.map(a => a.id)} strategy={verticalListSortingStrategy}>
                      {day.activities.length === 0 ? (
                        <EmptyDayState>
                          {userIsPartner(profile)
                            ? t("treatment.editor.no_activities")
                            : t("treatment.editor.drop_activity_here")}
                        </EmptyDayState>
                      ) : (
                        <DayActivitiesList $isDraggingFromLibrary={false}>
                          {sortedActivities.map((scheduleWeekActivity, activityIndex) => {
                            const activity = convertProtocolCardToSortable({
                              activity: scheduleWeekActivity,
                              day: dayNumber,
                              week: weekNumber,
                            });
                            const isDisabled = isBeforeCurrent || isCurrent || userIsPartner(profile);

                            return (
                              <ProtocolTimelineCard
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${activity.id}--${dayNumber}-${activityIndex}`}
                                activity={activity}
                                isDisabled={isDisabled}
                                smartReply={getSmartReply(activity.user_activity_id)}
                                smartReplyId={getSmartReplyId(activity.user_activity_id)}
                                week={weekNumber}
                                onPreview={
                                  ["exercise", "lesson"].includes(activity.activity_type_trimmed)
                                    ? () => handlePreview(activity)
                                    : undefined
                                }
                                onRemove={
                                  !isDisabled
                                    ? () => {
                                        if (activity.user_activity_schedule_id) {
                                          onRemoveProtocolActivity({
                                            patientID: patient.id,
                                            userActivityScheduleID: activity.user_activity_schedule_id as number,
                                          });
                                        }
                                      }
                                    : undefined
                                }
                              />
                            );
                          })}
                        </DayActivitiesList>
                      )}
                    </SortableContext>
                  </DayColumn>
                );
              })}
            </DaysContainer>
          </DaysScrollContainer>

          <DragOverlay dropAnimation={null}>
            {activeActivity && (
              <DragPreviewCard>
                <ProtocolTimelineCard
                  activity={activeActivity}
                  smartReply={null}
                  smartReplyId={null}
                  isDragging
                  week={activeActivity.week}
                />
              </DragPreviewCard>
            )}
          </DragOverlay>
        </CDndContext>
      </ContentContainer>
      {previewActivity && <ActivityPreviewModal activity={previewActivity} onClose={() => setPreviewActivity(null)} />}
    </Box>
  );
};

const DaysScrollContainer = styled.div<{ $isVertical: boolean }>`
  overflow-x: ${({ $isVertical }) => ($isVertical ? "hidden" : "auto")};
  overflow-y: ${({ $isVertical }) => ($isVertical ? "auto" : "hidden")};
  border-top: ${({ theme }) => theme.semantic.container.border};

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f9fa;
  }

  &::-webkit-scrollbar-thumb {
    background: #c5d0dd;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #8391a1;
  }
`;

const DaysContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1px;
  background-color: #e1e8ef;
  min-width: fit-content;

  &.auto {
    flex-wrap: wrap;
  }

  &.vertical {
    flex-direction: column;
  }
`;

const EmptyDayState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 80px;
  width: 100%;
  font-size: 12px;
  color: #8391a1;
  border: 2px dashed #d0d7de;
  border-radius: 6px;
  background-color: #fafbfc;
  text-align: center;
`;

const DayActivitiesList = styled.div<{ $isDraggingFromLibrary: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: ${({ $isDraggingFromLibrary }) => ($isDraggingFromLibrary ? "none" : "auto")};
  width: 100%;
  flex: 1;
  padding-bottom: 150px;
`;

const DragPreviewCard = styled.div`
  opacity: 0.95;
  transform: rotate(2deg);
  cursor: grabbing;
`;
